import React, { useState } from 'react';
import { Box, Flex, Text } from '@chakra-ui/react';
import NestedTabList from 'src/layouts/NestedTabList';
import { CopyRightsTab } from './CopyRightsTab';
import { ICMJETab } from './ICMJETab';
import { useSubmissionData } from '../../../contexts/SubmissionDataProvider';
import { useQuery } from '@tanstack/react-query';
import { fetchSubmissionContext } from '../../../api/queries';
import { isFormConfigured } from '../../../utils/formConfig';

export const RIGHTS_TABS = {
  COPYRIGHT: 'Copyright and Publication',
  ICMJE: 'Forms and Disclosures'
} as const;

export type RightsSubTab = (typeof RIGHTS_TABS)[keyof typeof RIGHTS_TABS];

const RightsTab: React.FC = () => {
  const { submissionContextId } = useSubmissionData();

  const { data } = useQuery({
    queryKey: ['submission-context', submissionContextId],
    queryFn: () =>
      submissionContextId ? fetchSubmissionContext(submissionContextId) : null
  });

  // useState must always be called unconditionally (Rules of Hooks)
  const [rightsSubTab, setRightsSubTab] = useState<RightsSubTab>(
    RIGHTS_TABS.COPYRIGHT
  );

  const isCopyrightConfigured = isFormConfigured(
    data?.config.authorCopyrightModel
  );
  const isICMJEConfigured = isFormConfigured(data?.config.authorICMJEModel);

  // CASE 0: Neither form is configured → show empty-state message
  if (!isCopyrightConfigured && !isICMJEConfigured) {
    return (
      <Flex gap={4} flexDirection="column">
        <Flex gap={2} flexDirection="column">
          <Text textStyle="headingSection">Rights</Text>
          <p>
            Review all forms required for the publication of your submission.
          </p>
        </Flex>
        <Text>No form available at the moment</Text>
      </Flex>
    );
  }

  // CASE 1: Only copyright
  if (isCopyrightConfigured && !isICMJEConfigured) {
    return (
      <Flex gap={4} flexDirection="column">
        <Flex gap={2} flexDirection="column">
          <Text textStyle="headingSection">Rights</Text>
          <p>
            Review all forms required for the publication of your submission.
          </p>
        </Flex>
        <CopyRightsTab isEditingAllowed={false} />
      </Flex>
    );
  }

  // CASE 2: Only ICMJE
  if (isICMJEConfigured && !isCopyrightConfigured) {
    return (
      <Flex gap={4} flexDirection="column">
        <Flex gap={2} flexDirection="column">
          <Text textStyle="headingSection">Rights</Text>
          <p>
            Review all forms required for the publication of your submission.
          </p>
        </Flex>
        <ICMJETab config={data!.config} />
      </Flex>
    );
  }

  // CASE 3: Both configured → tabbed UI
  return (
    <Flex gap={4}>
      <Flex gap={2} flexDirection="column">
        <Text textStyle="headingSection">Rights</Text>
        <p>Review all forms required for the publication of your submission.</p>
      </Flex>

      <NestedTabList>
        <Box marginX={16}>
          <NestedTabList.Navigation>
            {[RIGHTS_TABS.COPYRIGHT, RIGHTS_TABS.ICMJE].map(tab => (
              <NestedTabList.Tab
                key={tab}
                onClick={() => setRightsSubTab(tab)}
                isActive={rightsSubTab === tab}
              >
                {tab}
              </NestedTabList.Tab>
            ))}
          </NestedTabList.Navigation>
        </Box>

        {rightsSubTab === RIGHTS_TABS.COPYRIGHT && (
          <CopyRightsTab isEditingAllowed={false} />
        )}
        {rightsSubTab === RIGHTS_TABS.ICMJE && (
          <ICMJETab config={data!.config} />
        )}
      </NestedTabList>
    </Flex>
  );
};

export default RightsTab;
